import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  addTestArtist,
  deleteArtist,
  fetchArtistById,
  removeTestArtist,
  updateArtistAccountStatus,
} from '../../services/artistService';
import { useArtists } from '../../contexts/ArtistsContext';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--space-lg);
  align-items: flex-start;
  flex-wrap: wrap;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const Breadcrumb = styled.span`
  font-size: var(--font-size-xs);
  color: var(--color-text-light);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  font-size: var(--font-size-xxl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: var(--space-sm);
  flex-wrap: wrap;
`;

const ActionsWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'primary',
})`
  padding: var(--space-xs) var(--space-md);
  border-radius: var(--border-radius-md);
  border: 1px solid ${props => props.primary ? 'var(--color-periwinkle)' : 'var(--color-border)'};
  background: ${props => props.primary ? 'var(--color-periwinkle)' : 'white'};
  color: ${props => props.primary ? 'white' : 'var(--color-text-secondary)'};
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all var(--transition-speed) ease;

  &:hover {
    background: ${props => props.primary ? 'var(--color-accent)' : 'var(--color-primary-lighter)'};
    border-color: var(--color-accent);
    color: ${props => props.primary ? 'white' : 'var(--color-primary)'};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${props => props.primary ? 'var(--color-primary-light)' : 'var(--color-primary-lighter)'};
    border-color: var(--color-border-light);
    color: var(--color-text-light);
  }
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: calc(100% + var(--space-xxs));
  right: 0;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-medium);
  min-width: 200px;
  z-index: 10;
  padding: var(--space-xxs) 0;
`;

const ActionsMenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: var(--space-sm) var(--space-md);
  text-align: left;
  font-size: var(--font-size-sm);
  color: ${props => props.$destructive ? 'var(--color-error)' : 'var(--color-text)'};
  cursor: pointer;

  &:hover {
    background: ${props => props.$destructive ? 'rgba(180, 35, 24, 0.08)' : 'var(--color-primary-lighter)'};
    color: ${props => props.$destructive ? 'var(--color-error)' : 'var(--color-primary)'};
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--color-text-light);
    background: transparent;
  }
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--space-lg);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.section`
  background: white;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-border);
  padding: var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const SectionTitle = styled.h2`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-md);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xxs);
`;

const FieldLabel = styled.span`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-light);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const FieldValue = styled.span`
  font-size: var(--font-size-md);
  color: var(--color-text);
  font-weight: var(--font-weight-medium);
  word-break: break-word;
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: ${props => props.active ? 'rgba(130, 172, 216, 0.16)' : 'rgba(180, 83, 9, 0.12)'};
  color: ${props => props.active ? 'var(--color-accent)' : 'var(--color-warning-dark)'};
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const TestBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: ${props => props.active ? 'rgba(0, 24, 206, 0.08)' : 'rgba(93, 64, 34, 0.08)'};
  color: ${props => props.active ? 'var(--color-accent)' : 'var(--color-text-muted)'};
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const ChecklistTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);
`;

const RoleChip = styled.span`
  padding: var(--space-xxs) var(--space-sm);
  border-radius: var(--border-radius-sm);
  background: var(--color-primary-lighter);
  border: 1px solid var(--color-border);
  color: var(--color-primary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
`;

const FeedbackMessage = styled.p.withConfig({
  shouldForwardProp: (prop) => prop !== 'error',
})`
  margin: 0;
  font-size: var(--font-size-sm);
  color: ${props => props.error ? 'var(--color-error)' : 'var(--color-success)'};
`;

const LoadingState = styled.div`
  padding: var(--space-xl);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
`;

const ErrorState = styled.div`
  padding: var(--space-xl);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-error);
  background: rgba(180, 35, 24, 0.08);
  color: var(--color-error);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-md);
`;

const EmptyValue = styled.span`
  color: var(--color-text-light);
  font-size: var(--font-size-sm);
`;

const Checklist = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const ChecklistItem = styled.li`
  display: flex;
  gap: var(--space-sm);
  align-items: flex-start;
`;

const ChecklistStatus = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'complete',
})`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-top: 6px;
  background: ${props => props.complete ? 'var(--color-periwinkle)' : 'var(--color-border)'};
  box-shadow: ${props => props.complete ? '0 0 0 3px rgba(130, 172, 216, 0.18)' : 'none'};
`;

const ChecklistBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xxs);
`;

const ChecklistTitle = styled.span`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const ChecklistHint = styled.span`
  font-size: var(--font-size-xs);
  color: var(--color-text-light);
`;

const formatDate = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().toLocaleString();
  }

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  if (typeof value === 'number') {
    return new Date(value).toLocaleString();
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString();
    }
  }

  if (value.seconds) {
    return new Date(value.seconds * 1000).toLocaleString();
  }

  return null;
};

const formatCurrency = (value, currency = 'GBP') => {
  const numericValue = Number(value ?? 0);

  if (Number.isNaN(numericValue)) {
    return currency ? `${currency} 0` : '0';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(numericValue);
  } catch {
    return `${currency} ${numericValue.toFixed(2)}`;
  }
};

const ArtistDetail = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { refreshArtists } = useArtists();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [testArtistUpdating, setTestArtistUpdating] = useState(false);
  const [deleteUpdating, setDeleteUpdating] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef(null);
  const actionBusy = statusUpdating || testArtistUpdating || deleteUpdating;

  useEffect(() => {
    let isMounted = true;

    const loadArtist = async () => {
      setLoading(true);
      setError(null);
      setStatusError(null);
      setStatusMessage(null);
      setActionsOpen(false);

      try {
        const data = await fetchArtistById(artistId);
        if (isMounted) {
          setArtist(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Unable to load artist details.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadArtist();

    return () => {
      isMounted = false;
    };
  }, [artistId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setActionsOpen(false);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, []);

  const formattedDates = useMemo(() => {
    if (!artist) {
      return {};
    }

    return {
      createdAt: formatDate(artist.created_at),
      updatedAt: formatDate(artist.updated_at),
      lastLoginAt: formatDate(artist.last_login_at),
    };
  }, [artist]);

  const handleStatusChange = async (nextStatus) => {
    if (!artist || actionBusy) {
      return;
    }

    if (artist.account_active === nextStatus) {
      setActionsOpen(false);
      return;
    }

    const confirmationMessage = nextStatus
      ? 'Activate this artist account manually? This overrides the published artwork requirement, but their account setup must be complete.'
      : 'Deactivate this artist account? They will lose platform access.';
    const confirmed = typeof window !== 'undefined'
      ? window.confirm(confirmationMessage)
      : true;

    if (!confirmed) {
      setActionsOpen(false);
      return;
    }

    try {
      setStatusUpdating(true);
      setStatusError(null);
      setStatusMessage(null);
      setActionsOpen(false);
      await updateArtistAccountStatus(artist.id, nextStatus);
      setArtist((prev) => (prev ? { ...prev, account_active: nextStatus } : prev));
      await refreshArtists();
      setStatusMessage(nextStatus ? 'Account activated.' : 'Account deactivated.');
    } catch (err) {
      setStatusError(err.message || 'Failed to update account status.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleTestArtistChange = async (nextStatus) => {
    if (!artist || actionBusy) {
      return;
    }

    if (artist.isTestArtist === nextStatus) {
      setActionsOpen(false);
      return;
    }

    const confirmationMessage = nextStatus
      ? 'Add this artist to the test group? They will receive feedback-programme access, platform commission will be waived, and any billable subscription may be scheduled to cancel at period end.'
      : 'Remove this artist from the test group? Free test access ends immediately.';
    const confirmed = typeof window !== 'undefined'
      ? window.confirm(confirmationMessage)
      : true;

    if (!confirmed) {
      setActionsOpen(false);
      return;
    }

    try {
      setTestArtistUpdating(true);
      setStatusError(null);
      setStatusMessage(null);
      setActionsOpen(false);
      if (nextStatus) {
        await addTestArtist(artist.id);
      } else {
        await removeTestArtist(artist.id);
      }
      setArtist((prev) => (prev ? { ...prev, isTestArtist: nextStatus } : prev));
      await refreshArtists();
      setStatusMessage(nextStatus ? 'Artist added to the test group.' : 'Artist removed from the test group.');
    } catch (err) {
      setStatusError(err.message || 'Failed to update test group membership.');
    } finally {
      setTestArtistUpdating(false);
    }
  };

  const handleDeleteArtist = async () => {
    if (!artist || actionBusy) {
      return;
    }

    const artistLabel = artist.user_display_name || artist.user_id || artist.id;
    const confirmationTarget = artistLabel;
    const enteredValue = typeof window !== 'undefined'
      ? window.prompt(`Permanently delete ${artistLabel}? Type "${confirmationTarget}" or "${artist.id}" to confirm.`)
      : confirmationTarget;

    if (enteredValue === null) {
      setActionsOpen(false);
      return;
    }

    const normalizedValue = enteredValue.trim();
    const allowedValues = new Set([
      confirmationTarget,
      artist.id,
      artist.user_id,
    ].filter(Boolean));

    if (!allowedValues.has(normalizedValue)) {
      setActionsOpen(false);
      setStatusMessage(null);
      setStatusError('Deletion cancelled. The confirmation text did not match this artist.');
      return;
    }

    try {
      setDeleteUpdating(true);
      setStatusError(null);
      setStatusMessage(null);
      setActionsOpen(false);
      await deleteArtist(artist.id);
      await refreshArtists();
      navigate('/artists/existing', { replace: true });
    } catch (err) {
      setStatusError(err.message || 'Failed to delete artist.');
    } finally {
      setDeleteUpdating(false);
    }
  };

  const metrics = artist?.metrics || { pieces_sold: 0, total_value_sold: 0, platform_profit: 0 };
  const currencyCode = artist?.preferred_currency || 'GBP';
  const checklistItems = useMemo(() => {
    if (!artist) {
      return [];
    }

    return [
      {
        id: 'profile',
        title: 'Profile setup',
        hint: artist.profile_completed
          ? 'Profile is complete and ready to publish.'
          : 'Complete the profile so the artist can be promoted.',
        complete: Boolean(artist.profile_completed),
      },
      {
        id: 'payout',
        title: 'Payout method',
        hint: artist.payout_method
          ? `Configured as ${artist.payout_method}.`
          : 'Add a payout method to enable revenue distribution.',
        complete: Boolean(artist.payout_method),
      },
      {
        id: 'portfolio',
        title: 'Portfolio link',
        hint: artist.portfolio_url
          ? artist.portfolio_url
          : 'Link a portfolio so curators can review recent work.',
        complete: Boolean(artist.portfolio_url),
      },
      {
        id: 'roles',
        title: 'Team visibility',
        hint: artist.roles && artist.roles.length
          ? 'Roles assigned for platform access.'
          : 'Assign roles to control platform permissions.',
        complete: Boolean(artist.roles && artist.roles.length),
        roles: artist.roles || [],
      },
    ];
  }, [artist]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Loading artist information...</LoadingState>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Header>
          <TitleGroup>
            <Breadcrumb>Existing Artists</Breadcrumb>
            <Title>Artist account</Title>
            <Subtitle>Manage account access and contact information.</Subtitle>
          </TitleGroup>
          <HeaderActions>
            <Button
              onClick={() => {
                setActionsOpen(false);
                navigate(-1);
              }}
            >
              Back
            </Button>
          </HeaderActions>
        </Header>
        <ErrorState role="alert">
          <span>{error}</span>
          <Button onClick={() => navigate('/artists/existing')}>View all artists</Button>
        </ErrorState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <TitleGroup>
          <Breadcrumb>Existing Artists</Breadcrumb>
          <Title>{artist.user_display_name}</Title>
          <Subtitle>Manage account access, contact details, and activity for this artist.</Subtitle>
        </TitleGroup>
        <HeaderActions>
          <Button
            onClick={() => {
              setActionsOpen(false);
              navigate(-1);
            }}
            disabled={actionBusy}
          >
            Back
          </Button>
          <ActionsWrapper ref={actionsRef}>
            <Button
              primary
              type="button"
              onClick={() => setActionsOpen((prev) => !prev)}
              disabled={actionBusy}
              aria-haspopup="menu"
              aria-expanded={actionsOpen}
            >
              {deleteUpdating ? 'Deleting...' : actionBusy ? 'Updating...' : 'Account actions'}
            </Button>
            {actionsOpen && (
              <ActionsMenu role="menu">
                <ActionsMenuItem
                  type="button"
                  role="menuitem"
                  onClick={() => handleStatusChange(!artist.account_active)}
                  disabled={actionBusy}
                >
                  {artist.account_active ? 'Deactivate account' : 'Activate account'}
                </ActionsMenuItem>
                <ActionsMenuItem
                  type="button"
                  role="menuitem"
                  onClick={() => handleTestArtistChange(!artist.isTestArtist)}
                  disabled={actionBusy}
                >
                  {artist.isTestArtist ? 'Remove from test group' : 'Add to test group'}
                </ActionsMenuItem>
                <ActionsMenuItem
                  type="button"
                  role="menuitem"
                  onClick={handleDeleteArtist}
                  disabled={actionBusy}
                  $destructive
                >
                  Delete artist
                </ActionsMenuItem>
              </ActionsMenu>
            )}
          </ActionsWrapper>
        </HeaderActions>
      </Header>

      {(statusError || statusMessage) && (
        <FeedbackMessage error={Boolean(statusError)} role={statusError ? 'alert' : 'status'}>
          {statusError || statusMessage}
        </FeedbackMessage>
      )}

      <LayoutGrid>
        <Section>
          <SectionTitle>Account Overview</SectionTitle>
          <FieldsGrid>
            <Field>
              <FieldLabel>Account status</FieldLabel>
              <FieldValue>
                <StatusBadge active={artist.account_active}>
                  {artist.account_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>Test group</FieldLabel>
              <FieldValue>
                <TestBadge active={artist.isTestArtist}>
                  {artist.isTestArtist ? 'Test artist' : 'Standard artist'}
                </TestBadge>
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>Artist ID</FieldLabel>
              <FieldValue>{artist.id}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>User ID</FieldLabel>
              <FieldValue>{artist.user_id || <EmptyValue>Not set</EmptyValue>}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Joined</FieldLabel>
              <FieldValue>{formattedDates.createdAt || <EmptyValue>Unknown</EmptyValue>}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Last sign-in</FieldLabel>
              <FieldValue>{formattedDates.lastLoginAt || <EmptyValue>No recent activity</EmptyValue>}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Last updated</FieldLabel>
              <FieldValue>{formattedDates.updatedAt || <EmptyValue>Unknown</EmptyValue>}</FieldValue>
            </Field>
          </FieldsGrid>
        </Section>

        <Section>
          <SectionTitle>Financials</SectionTitle>
          <FieldsGrid>
            <Field>
              <FieldLabel>Number of pieces sold</FieldLabel>
              <FieldValue>{metrics.pieces_sold ?? 0}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Total value sold</FieldLabel>
              <FieldValue>{formatCurrency(metrics.total_value_sold, currencyCode)}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Platform profit generated</FieldLabel>
              <FieldValue>{formatCurrency(metrics.platform_profit, currencyCode)}</FieldValue>
            </Field>
          </FieldsGrid>
        </Section>
      </LayoutGrid>

      <LayoutGrid>
        <Section>
          <SectionTitle>Contact Information</SectionTitle>
          <FieldsGrid>
            <Field>
              <FieldLabel>Bio</FieldLabel>
              <FieldValue>{artist.bio || <EmptyValue>No bio provided</EmptyValue>}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Email</FieldLabel>
              <FieldValue>
                {artist.user_email ? (
                  <a href={`mailto:${artist.user_email}`}>{artist.user_email}</a>
                ) : (
                  <EmptyValue>Not provided</EmptyValue>
                )}
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>Phone number</FieldLabel>
              <FieldValue>{artist.phone_number || <EmptyValue>Not provided</EmptyValue>}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Location</FieldLabel>
              <FieldValue>{[artist.city, artist.country].filter(Boolean).join(', ') || <EmptyValue>Not specified</EmptyValue>}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Preferred currency</FieldLabel>
              <FieldValue>{artist.preferred_currency || <EmptyValue>Not set</EmptyValue>}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Portfolio</FieldLabel>
              <FieldValue>
                {artist.portfolio_url ? (
                  <a href={artist.portfolio_url} target="_blank" rel="noopener noreferrer">
                    {artist.portfolio_url}
                  </a>
                ) : (
                  <EmptyValue>Not provided</EmptyValue>
                )}
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>Instagram</FieldLabel>
              <FieldValue>
                {artist.instagram_handle ? (
                  artist.instagram_handle.startsWith('@')
                    ? artist.instagram_handle
                    : `@${artist.instagram_handle}`
                ) : (
                  <EmptyValue>Not linked</EmptyValue>
                )}
              </FieldValue>
            </Field>
          </FieldsGrid>
        </Section>

        <Section>
          <SectionTitle>Account Checklist</SectionTitle>
          {checklistItems.length === 0 ? (
            <EmptyValue>No checklist items available.</EmptyValue>
          ) : (
            <Checklist>
              {checklistItems.map((item) => (
                <ChecklistItem key={item.id}>
                  <ChecklistStatus complete={item.complete} />
                  <ChecklistBody>
                    <ChecklistTitle>{item.title}</ChecklistTitle>
                    <ChecklistHint>{item.hint}</ChecklistHint>
                    {item.roles && item.roles.length > 0 && (
                      <ChecklistTags>
                        {item.roles.map((role) => (
                          <RoleChip key={role}>{role}</RoleChip>
                        ))}
                      </ChecklistTags>
                    )}
                  </ChecklistBody>
                </ChecklistItem>
              ))}
            </Checklist>
          )}
        </Section>
      </LayoutGrid>
    </PageContainer>
  );
};

export default ArtistDetail;
